/**
 * Coverage by category: each category's %, and each dollar maximum said once.
 * A maximum shared by categories that sit next to each other is ONE cell across
 * their rows ("$1,000 per year", "Preventative and Basic share it") — repeating
 * it on every row read as $1,000 each. One shared across a gap is stated on
 * each of its rows, naming the others. Under each amount, the carrier's own
 * sentence for it, in full. The reset date is in Plan details.
 *
 * "Edit coverage" edits the table where it stands: each % and each maximum and
 * remaining become fields in their own cells, one control per cell on the
 * row's middle line (a shared maximum is still one cell, saved for every
 * category that shares it); the ortho terms get a line under the Ortho row,
 * and which categories share a maximum is chosen under the table. Beside each
 * % sits the status the % can't say — "Predetermination required" when the
 * carrier confirmed a category but gave no rate; it starts as what the
 * category's lines say — and a note under it.
 * One Save. A category's figures are saved as this breakdown's override (the
 * carrier's values stay underneath — "Use the carrier's" brings them back);
 * the shared maximum and the ortho terms are the plan's, saved on the policy.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, DatePicker, Input, Select, Tag, message } from "antd";
import dayjs from "dayjs";

import { UNLIMITED_SENTINEL as UNLIMITED } from "../../../lib/categoryValue";
import { LINE_CHOICES, PLAN_DETAIL_ROWS, choiceLabel, statusFromPercent } from "../../../lib/planDetails";
import ClampedText from "./ClampedText";
import { rowCode } from "../../../lib/procedureFamilies";
import { categoryCoverageWords, coverageTableRows, dollars, orthoWords, poolAmountWords, poolWhoWords } from "./sheetModel";
import "./breakdownSheet.css";

const CATS = ["Preventative", "Basic", "Major", "Orthodontics"];
const ORTHO_FIELDS = ["ortho_age_min", "ortho_age_max", "ortho_payment", "ortho_initial_payment_pct"];
const MAX_KINDS = [{ value: "annual", label: "per year" }, { value: "lifetime", label: "lifetime" }, { value: "none", label: "No dollar maximum" }];
const YEAR_KINDS = [{ value: "calendar_year", label: "Jan 1 (calendar year)" }, { value: "policy_year", label: "On a benefit year date" }];
const SCOPES = [{ value: "per_person", label: "Per person" }, { value: "per_family", label: "Per family" }];

function names(list) {
  if (list.length <= 1) return list.join("");
  return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
}

function MaximumCell({ row }) {
  const pool = row.pool;
  if (!pool) return <span className="none">—</span>;
  const who = row.span > 1 ? poolWhoWords(pool) : row.sharedWith.length ? `Shared with ${names(row.sharedWith)}` : "";
  const sub = [who, pool.poolId].filter(Boolean).join(" · ");
  return (
    <>
      <span>{poolAmountWords(pool)}</span>
      {sub ? <div className="sub"><ClampedText text={sub} /></div> : null}
      {/* The carrier's own words, all of them: they say what the maximum covers. */}
      {pool.note ? <div className="said">{pool.note}</div> : null}
    </>
  );
}

// A figure as the field shows it: "90.00" → "90", none → "".
function figure(v) {
  if (v == null || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : "";
}

// The status a % implies, as the status field's placeholder ("Covered (from the %)").
function statusPlaceholder(pct) {
  const implied = statusFromPercent(pct, "");
  return implied ? `${choiceLabel(LINE_CHOICES.coverage_status, implied)} (from the %)` : "Status";
}
const isUnlimited = (v) => v != null && v !== "" && Number(v) >= Number(UNLIMITED);
const maxKey = (row) => (row.pool ? row.pool.cats.join("+") : row.cat);

/** The fields' starting values, from what the table shows. */
export function coverageDraft({ categories, sharedMaxGroups, policy, table }) {
  const cards = categories || {};
  const pct = {};
  const status = {};
  const note = {};
  const max = {};
  CATS.forEach((cat) => {
    const card = cards[cat.toLowerCase()] || {};
    pct[cat] = figure(card.coverage);
    // What the % can't say ("Predetermination required"), and a note under it.
    status[cat] = card.status || "";
    note[cat] = card.note || "";
  });
  table.forEach((r) => {
    const key = maxKey(r);
    if (max[key]) return;
    // The figures the cell shows: the pool's (its first category with a
    // maximum), else the category's own.
    if (r.pool) {
      const withMax = r.pool.cats.map((c) => cards[c.toLowerCase()] || {}).find((c) => c.overall_max != null && c.overall_max !== "") || {};
      max[key] = {
        cats: r.pool.cats.slice(), unlimited: !!r.pool.unlimited, amount: figure(r.pool.amount), remaining: figure(r.pool.remaining),
        period: withMax.period === "lifetime" ? "lifetime" : "annual",
      };
      return;
    }
    const card = cards[r.cat.toLowerCase()] || {};
    max[key] = {
      cats: [r.cat],
      unlimited: isUnlimited(card.overall_max),
      amount: isUnlimited(card.overall_max) ? "" : figure(card.overall_max),
      remaining: isUnlimited(card.benefits_remaining) ? "" : figure(card.benefits_remaining),
      period: card.period === "lifetime" ? "lifetime" : "annual",
    };
  });
  const firstGroup = (sharedMaxGroups || []).find((g) => Array.isArray(g) && g.length > 1) || [];
  const p = policy || {};
  const ortho = {};
  ORTHO_FIELDS.forEach((f) => { ortho[f] = p[f] == null ? "" : String(p[f]); });
  // When yearly maximums reset, and who they're for: the plan's own fields
  // (Plan details shows the same answer).
  const bp = p.benefit_period && p.benefit_period.start ? p.benefit_period : {};
  const plan = {
    yearType: (bp.type || p.benefit_period_type || "") === "unknown" ? "" : (bp.type || p.benefit_period_type || ""),
    yearStart: String(bp.start || p.benefit_year_start || "").slice(0, 10),
    maxScope: p.max_scope && p.max_scope !== "unknown" ? p.max_scope : "",
  };
  return { pct, status, note, max, shared: CATS.filter((c) => firstGroup.indexOf(c) !== -1), ortho, plan, revert: [] };
}

/**
 * The benefit year containing `today` for a plan whose year starts on
 * `start`'s month and day; a calendar year is Jan 1 – Dec 31. ISO dates.
 */
export function benefitYearAround(type, start, today) {
  const now = dayjs(today || undefined).startOf("day");
  if (type === "calendar_year") return { start: `${now.year()}-01-01`, end: `${now.year()}-12-31` };
  const s = dayjs(start);
  if (type !== "policy_year" || !s.isValid()) return null;
  let first = dayjs(new Date(now.year(), s.month(), s.date()));
  if (first.isAfter(now)) first = first.subtract(1, "year");
  return { start: first.format("YYYY-MM-DD"), end: first.add(1, "year").subtract(1, "day").format("YYYY-MM-DD") };
}

function numberOk(v, { max } = {}) {
  if (v === "" || v == null) return true;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && (max == null || n <= max) && !/\.\d{3,}$/.test(String(v));
}

/**
 * What Save sends: `set` — the categories whose figures changed, each frozen
 * whole ({percentage, overall_max, benefits_remaining, period, status, note}); `revert` — categories
 * going back to the carrier's figures; `policy` — the shared maximum, the ortho
 * terms, when yearly maximums reset and who they're for, when they changed.
 * Returns {error} when a value can't be saved.
 */
export function coverageChanges(start, draft, sharedMaxGroups, today) {
  for (const cat of CATS) {
    if (!numberOk(draft.pct[cat], { max: 100 })) return { error: `${cat}: coverage must be 0–100%` };
    // The line editor's rule: a rate above 0 is coverage.
    if (draft.status[cat] === "not_covered" && Number(draft.pct[cat]) > 0) {
      return { error: `${cat}: Not covered can't also have a coverage %. Clear the % or change the status` };
    }
  }
  for (const key of Object.keys(draft.max)) {
    const m = draft.max[key];
    if (!numberOk(m.amount) || !numberOk(m.remaining)) return { error: "Amounts must be dollars, 0 or more" };
  }
  if (draft.shared.length === 1) return { error: "A shared maximum needs two or more categories — pick another, or none" };
  const poolOf = (cat) => Object.keys(draft.max).find((k) => draft.max[k].cats.indexOf(cat) !== -1);
  const set = {};
  CATS.forEach((cat) => {
    if (draft.revert.indexOf(cat) !== -1) return;
    const key = poolOf(cat);
    const now = draft.max[key] || {};
    const was = start.max[key] || {};
    const changed = draft.pct[cat] !== start.pct[cat] || now.amount !== was.amount
      || now.unlimited !== was.unlimited || now.remaining !== was.remaining || now.period !== was.period
      || draft.status[cat] !== start.status[cat] || draft.note[cat] !== start.note[cat];
    if (changed) {
      set[cat.toLowerCase()] = {
        percentage: draft.pct[cat],
        overall_max: now.unlimited ? UNLIMITED : now.amount,
        benefits_remaining: now.remaining,
        period: now.unlimited ? null : now.period || "annual",
        status: draft.status[cat] || null,
        note: draft.note[cat].trim() || null,
      };
    }
  });
  const policy = {};
  ORTHO_FIELDS.forEach((f) => {
    if (draft.ortho[f] !== start.ortho[f]) policy[f] = draft.ortho[f] === "" ? null : draft.ortho[f];
  });
  if (draft.shared.join("+") !== start.shared.join("+")) {
    policy.category_shared_max_groups = draft.shared.length ? [draft.shared] : [];
  }
  const plan = draft.plan || {};
  const was = start.plan || {};
  if (plan.yearType !== was.yearType || (plan.yearType === "policy_year" && plan.yearStart !== was.yearStart)) {
    if (plan.yearType === "policy_year" && !plan.yearStart) return { error: "A benefit year needs the date it starts" };
    const year = benefitYearAround(plan.yearType, plan.yearStart, today);
    policy.benefit_period_type = plan.yearType || null;
    policy.benefit_year_start = year ? year.start : null;
    policy.benefit_year_end = year ? year.end : null;
  }
  if (plan.maxScope !== was.maxScope) policy.max_scope = plan.maxScope || null;
  return { set, revert: draft.revert.map((c) => c.toLowerCase()), policy, sharedMaxGroups };
}

/**
 * `startEditing` (the Add Manually wizard's last step): open in edit mode as
 * soon as the figures arrive, with the cells still empty marked, and expose
 * `{ dirty(), save() }` on `controlRef` so the wizard's Finish saves them.
 * `noDollarMaximum` (a program with none, CDCP): offer it for every maximum.
 */
export default function CoverageTable({
  policy, categories, sharedMaxGroups, rows, onEditCoverage, onSaveCoverage, startEditing = false, controlRef,
  noDollarMaximum = null,
}) {
  const table = useMemo(() => coverageTableRows({ categories, sharedMaxGroups, rows, policy }),
    [categories, sharedMaxGroups, rows, policy]);
  const ortho = orthoWords(policy);
  const [start, setStart] = useState(null);
  const [draft, setDraft] = useState(null);
  // The table's layout when the edit began. Saving refetches the figures while
  // the fields are still up; a new shared maximum merges rows, and the live
  // layout's cells would no longer match the draft (that crashed the page).
  const [editTable, setEditTable] = useState(null);
  const [saving, setSaving] = useState(false);
  // Categories whose note field is open without a note yet ("Add a note").
  const [notesOpen, setNotesOpen] = useState([]);
  const editing = !!draft;
  const several = (sharedMaxGroups || []).filter((g) => Array.isArray(g) && g.length > 1).length > 1;

  function beginEdit() {
    const d = coverageDraft({ categories, sharedMaxGroups, policy, table });
    setEditTable(table);
    setStart(d);
    setDraft(JSON.parse(JSON.stringify(d)));
    setNotesOpen([]);
  }
  function stopEdit() { setStart(null); setDraft(null); setEditTable(null); setNotesOpen([]); }
  const update = (fn) => setDraft((prev) => { const next = JSON.parse(JSON.stringify(prev)); fn(next); return next; });

  function pendingChanges() {
    const changes = coverageChanges(start, draft, sharedMaxGroups);
    if (changes.error) return changes;
    // With an ortho line, its terms are answered there, not here.
    if (orthoLine) ORTHO_FIELDS.forEach((f) => { delete changes.policy[f]; });
    changes.nothing = !Object.keys(changes.set).length && !changes.revert.length && !Object.keys(changes.policy).length;
    return changes;
  }

  // Returns whether everything is saved (nothing to save counts).
  async function save() {
    if (!draft) return true;
    const changes = pendingChanges();
    if (changes.error) { message.error(changes.error); return false; }
    if (changes.nothing) { stopEdit(); return true; }
    setSaving(true);
    try {
      const ok = await onSaveCoverage(changes);
      if (ok) { message.success("Changes saved"); stopEdit(); }
      return !!ok;
    } finally {
      setSaving(false);
    }
  }

  // The wizard opens the table editing, once, when its figures are in.
  const began = useRef(false);
  useEffect(() => {
    if (!startEditing || began.current || !categories) return;
    began.current = true;
    beginEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startEditing, categories]);
  if (controlRef) {
    controlRef.current = {
      dirty: () => { if (!draft) return false; const c = pendingChanges(); return !!c.error || !c.nothing; },
      save,
    };
  }
  const empty = (v) => startEditing && (v == null || String(v).trim() === "");

  const money = (value, onChange, label, disabled) => (
    <Input type="number" min={0} prefix="$" value={value} disabled={disabled}
      onChange={(e) => onChange(e.target.value)} aria-label={label} />
  );
  const orthoRow = (f) => PLAN_DETAIL_ROWS.find((r) => r.field === f) || {};
  // The breakdown's answered ortho line, if any (its terms are set there).
  const orthoLine = useMemo(() => {
    const line = (rows || []).find((r) => r && !r.data_unavailable && /^(8|93)/.test(rowCode(r)));
    return line ? rowCode(line) : "";
  }, [rows]);
  const setOrtho = (f) => (e) => { const v = e && e.target ? e.target.value : e; update((d) => { d.ortho[f] = v == null ? "" : v; }); };

  return (
    <section className="bs-ctab-sec" aria-label="Coverage by category" data-testid="breakdown-coverage" data-onboarding-id="bd-finance-cards">
      {/* In the wizard its own heading and Finish stand in for these. */}
      {startEditing ? null : (
      <div className="bs-sec-head">
        <h3 className="bs-h3">Coverage by category</h3>
        {editing ? (
          <span className="bs-ctab-bar">
            <Button size="small" onClick={stopEdit} disabled={saving}>Cancel</Button>
            <Button size="small" type="primary" onClick={save} loading={saving}>Save changes</Button>
          </span>
        ) : onSaveCoverage ? (
          <Button size="small" className="bs-col-act" onClick={beginEdit}>Edit coverage</Button>
        ) : onEditCoverage ? (
          <Button size="small" className="bs-col-act" onClick={onEditCoverage}>Edit coverage</Button>
        ) : null}
      </div>
      )}
      <table className={`bs-ctab${editing ? " editing bs-fields" : ""}`}>
        <colgroup><col style={{ width: "22%" }} /><col style={{ width: "22%" }} /><col style={{ width: "36%" }} /><col style={{ width: "20%" }} /></colgroup>
        <thead><tr><th>Category</th><th>Coverage</th><th>Maximum</th><th>Remaining</th></tr></thead>
        <tbody>
          {(editing && editTable ? editTable : table).map((r) => {
            const words = categoryCoverageWords(r);
            const merged = r.span > 1;
            const left = r.pool && r.pool.remaining != null && !r.pool.unlimited ? dollars(r.pool.remaining) : null;
            if (editing) {
              const key = maxKey(r);
              const m = draft.max[key] || { cats: [r.cat], unlimited: false, amount: "", remaining: "", period: "annual" };
              const reverting = draft.revert.indexOf(r.cat) !== -1;
              const isOrtho = r.cat === "Orthodontics";
              const rowEl = (
                <tr key={r.cat} data-cat={r.cat} className={isOrtho ? "has-terms" : undefined}>
                  <td className="cat">
                    {r.cat}
                    {r.overridden ? (
                      <button type="button" className="bs-link revert"
                        onClick={() => update((d) => {
                          d.revert = reverting ? d.revert.filter((c) => c !== r.cat) : d.revert.concat(r.cat);
                        })}>
                        {reverting ? "Keep the edit" : "Use the carrier's"}
                      </button>
                    ) : null}
                  </td>
                  <td>
                    {/* The %, the status beside it (a status alone is an answer: the
                        cell isn't empty), and the note on a line of its own. */}
                    <div className={`bs-ctab-cell bs-ctab-coverage${empty(draft.pct[r.cat]) && !draft.status[r.cat] ? " empty" : ""}`}>
                      <Input type="number" min={0} max={100} suffix="%" value={draft.pct[r.cat]}
                        disabled={reverting} placeholder="0 = not covered"
                        onChange={(e) => { const v = e.target.value; update((d) => { d.pct[r.cat] = v; }); }}
                        aria-label={`${r.cat} coverage`} />
                      <Select className="bs-ctab-status" allowClear disabled={reverting} options={LINE_CHOICES.coverage_status}
                        value={draft.status[r.cat] || undefined} placeholder={statusPlaceholder(draft.pct[r.cat])}
                        aria-label={`${r.cat} status`}
                        onChange={(v) => update((d) => { d.status[r.cat] = v || ""; })} />
                      {draft.note[r.cat] || notesOpen.indexOf(r.cat) !== -1 ? (
                        <Input className="bs-ctab-note" maxLength={200} value={draft.note[r.cat]} disabled={reverting}
                          placeholder="A note under the coverage (what the carrier said)" aria-label={`${r.cat} note`}
                          onChange={(e) => { const v = e.target.value; update((d) => { d.note[r.cat] = v; }); }} />
                      ) : (
                        <button type="button" className="bs-link bs-ctab-addnote" disabled={reverting}
                          onClick={() => setNotesOpen((open) => open.concat(r.cat))}>Add a note</button>
                      )}
                    </div>
                  </td>
                  {r.span === 0 ? null : (
                    <>
                      <td rowSpan={merged ? r.span : undefined} className={merged ? "pool" : undefined}>
                        <div className={`bs-ctab-cell${!m.unlimited && empty(m.amount) ? " empty" : ""}`}>
                          {money(m.amount, (v) => update((d) => { d.max[key].amount = v; }), `${r.cat} maximum`, m.unlimited || reverting)}
                          <Select className="bs-ctab-kind" disabled={reverting} options={MAX_KINDS}
                            value={m.unlimited ? "none" : m.period || "annual"} aria-label={`${r.cat} maximum is`}
                            onChange={(v) => update((d) => {
                              d.max[key].unlimited = v === "none";
                              if (v !== "none") d.max[key].period = v;
                            })} />
                        </div>
                      </td>
                      <td rowSpan={merged ? r.span : undefined} className={merged ? "pool" : undefined}>
                        <div className="bs-ctab-cell">
                          {money(m.remaining, (v) => update((d) => { d.max[key].remaining = v; }), `${r.cat} remaining`, m.unlimited || reverting)}
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              );
              if (!isOrtho) return rowEl;
              // The ortho terms, on one line under the Ortho row: read off the
              // ortho line when the breakdown has one (answered there once).
              if (orthoLine) {
                return [rowEl, (
                  <tr key="ortho-terms" className="bs-ctab-terms">
                    <td />
                    <td colSpan={3}>
                      <div className="bs-ctab-cell">
                        <span>{ortho || "No ages or payment terms recorded"}</span>
                        <span className="bs-ctab-from">{`Set on the ${orthoLine} line`}</span>
                      </div>
                    </td>
                  </tr>
                )];
              }
              return [rowEl, (
                <tr key="ortho-terms" className="bs-ctab-terms">
                  <td />
                  <td colSpan={3}>
                    <div className="bs-ctab-cell">
                      <span className="term">Ages</span>
                      <Input type="number" min={0} placeholder="From" value={draft.ortho.ortho_age_min}
                        onChange={setOrtho("ortho_age_min")} aria-label="Ortho from age" />
                      <span className="term">to</span>
                      <Input type="number" min={0} placeholder="To" value={draft.ortho.ortho_age_max}
                        onChange={setOrtho("ortho_age_max")} aria-label="Ortho to age" />
                      <span className="term">Payment</span>
                      <Select allowClear placeholder="How it's paid" options={orthoRow("ortho_payment").options}
                        value={draft.ortho.ortho_payment || undefined}
                        onChange={setOrtho("ortho_payment")} aria-label="Ortho payment" />
                      <span className="term">Paid on the initial fee</span>
                      <Input type="number" min={0} max={100} suffix="%" value={draft.ortho.ortho_initial_payment_pct}
                        onChange={setOrtho("ortho_initial_payment_pct")} aria-label="Paid on the initial fee" />
                    </div>
                  </td>
                </tr>
              )];
            }
            return (
              <tr key={r.cat} data-cat={r.cat}>
                <td className="cat">{r.cat}</td>
                <td className={!words.pct && !words.status ? "none" : r.pct === 0 || r.status === "not_covered" ? "no" : undefined}>
                  {words.pct ? words.pct : words.status ? <span className={`bs-chip ${words.status.tone}`}>{words.status.label}</span> : "—"}
                  {r.overridden ? <span className="edited">edited</span> : null}
                  {words.pct && words.status ? <div className="sub"><span className={`bs-chip ${words.status.tone}`}>{words.status.label}</span></div> : null}
                  {words.note ? <div className="sub"><ClampedText text={words.note} /></div> : null}
                  {r.cat === "Orthodontics" && ortho ? <div className="sub"><ClampedText text={ortho} /></div> : null}
                </td>
                {r.span === 0 ? null : (
                  <>
                    <td rowSpan={merged ? r.span : undefined} className={merged ? "pool" : undefined}><MaximumCell row={r} /></td>
                    <td rowSpan={merged ? r.span : undefined} className={[merged ? "pool" : "", left ? "" : "none"].filter(Boolean).join(" ") || undefined}>
                      {left || "—"}
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {editing && noDollarMaximum ? (
        <div className="bs-ctab-program">
          <span>{`${noDollarMaximum} has no dollar maximum.`}</span>
          <button type="button" className="bs-link" onClick={() => update((d) => {
            Object.keys(d.max).forEach((k) => { d.max[k].unlimited = true; d.max[k].amount = ""; d.max[k].remaining = ""; });
          })}>Use for every category</button>
        </div>
      ) : null}
      {editing ? (
        <div className="bs-ctab-shared">
          <span>One maximum shared by</span>
          {CATS.map((c) => (
            <Tag.CheckableTag key={c} checked={draft.shared.indexOf(c) !== -1}
              onChange={(on) => update((d) => {
                d.shared = on ? CATS.filter((x) => x === c || d.shared.indexOf(x) !== -1) : d.shared.filter((x) => x !== c);
              })}>
              {c}
            </Tag.CheckableTag>
          ))}
          <span>{draft.shared.length ? "" : "None: each category has its own."}</span>
          {several ? <span>(replaces both pools recorded now)</span> : null}
        </div>
      ) : null}
      {editing ? (
        <div className="bs-ctab-plan bs-fields">
          <span>Yearly maximums reset</span>
          <Select className="bs-ctab-year" allowClear placeholder="Ask" options={YEAR_KINDS} value={draft.plan.yearType || undefined}
            aria-label="Yearly maximums reset" onChange={(v) => update((d) => { d.plan.yearType = v || ""; })} />
          {draft.plan.yearType === "policy_year" ? (
            <DatePicker className="bs-ctab-date" format="MMM D" placeholder="Starts on" aria-label="Benefit year starts"
              value={draft.plan.yearStart && dayjs(draft.plan.yearStart).isValid() ? dayjs(draft.plan.yearStart) : null}
              onChange={(v) => update((d) => { d.plan.yearStart = v ? v.format("YYYY-MM-DD") : ""; })} />
          ) : null}
          <span>Maximums apply</span>
          <Select className="bs-ctab-scope" allowClear placeholder="Ask" options={SCOPES} value={draft.plan.maxScope || undefined}
            aria-label="Maximums apply" onChange={(v) => update((d) => { d.plan.maxScope = v || ""; })} />
        </div>
      ) : null}
    </section>
  );
}
